import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CustomerOrdersAPI, PublicCartAPI } from '../../Services/ECommerceAPI';
import { notifyCartUpdated } from '../../Layout/PublicLayout';
import { toast } from 'react-toastify';
import './Checkout.css';

const Checkout = () => {
    const location = useLocation();
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const [guestSessionId, setGuestSessionId] = useState(null);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        shippingFirstName: '',
        shippingLastName: '',
        shippingAddress: '',
        shippingCity: '',
        shippingState: '',
        shippingPostalCode: '',
        shippingCountry: '',
        shippingPhone: '',
        billingSameAsShipping: true,
        billingFirstName: '',
        billingLastName: '',
        billingAddress: '',
        billingCity: '',
        billingState: '',
        billingPostalCode: '',
        billingCountry: '',
        paymentMethod: 'Credit Card',
        shippingMethod: 'Standard',
        notes: ''
    });

    useEffect(() => {
        loadCart();
    }, []);

    const getGuestSessionId = () => {
        const fromUrl = new URLSearchParams(location.search).get('sid');
        if (fromUrl) return decodeURIComponent(fromUrl);
        if (location.state?.guestSessionId) return location.state.guestSessionId;
        return sessionStorage.getItem('cartSessionId');
    };

    const loadCart = async () => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');
            let cartData;

            if (token) {
                cartData = await CustomerOrdersAPI.getCart();
                setIsGuest(false);
            } else {
                const sessionId = getGuestSessionId();
                if (!sessionId || typeof sessionId !== 'string' || !sessionId.trim()) {
                    toast.error('Your cart is empty');
                    navigate('/cart');
                    return;
                }
                const trimmedSessionId = sessionId.trim();
                sessionStorage.setItem('cartSessionId', trimmedSessionId);
                cartData = await PublicCartAPI.getCart(trimmedSessionId);
                setIsGuest(true);
                setGuestSessionId(trimmedSessionId);
            }

            const items = Array.isArray(cartData?.cartItems) ? cartData.cartItems : (Array.isArray(cartData?.CartItems) ? cartData.CartItems : []);
            const cartWithItems = cartData ? { ...cartData, cartItems: items } : null;
            setCart(cartWithItems);

            if (!cartWithItems || items.length === 0) {
                toast.error('Your cart is empty');
                navigate('/cart');
            }
        } catch (error) {
            console.error('Error loading cart:', error);
            toast.error(error.response?.data?.message || 'Failed to load cart');
            navigate('/cart');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Auto-fill billing if same as shipping
        if (name === 'billingSameAsShipping' && checked) {
            setFormData(prev => ({
                ...prev,
                billingFirstName: prev.shippingFirstName,
                billingLastName: prev.shippingLastName,
                billingAddress: prev.shippingAddress,
                billingCity: prev.shippingCity,
                billingState: prev.shippingState,
                billingPostalCode: prev.shippingPostalCode,
                billingCountry: prev.shippingCountry
            }));
        }
    };

    const calculateSubtotal = () => {
        if (!cart || !cart.cartItems) return 0;
        return cart.cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const tax = subtotal * 0.1;
        const shipping = subtotal > 100 ? 0 : 10;
        return subtotal + tax + shipping;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.shippingFirstName || !formData.shippingLastName || !formData.shippingAddress) {
            toast.error('Please fill in all required shipping fields');
            return;
        }

        try {
            setProcessing(true);
            const orderPayload = {
                shippingFirstName: formData.shippingFirstName,
                shippingLastName: formData.shippingLastName,
                shippingAddress: formData.shippingAddress,
                shippingCity: formData.shippingCity,
                shippingState: formData.shippingState,
                shippingPostalCode: formData.shippingPostalCode,
                shippingCountry: formData.shippingCountry,
                shippingPhone: formData.shippingPhone,
                billingFirstName: formData.billingFirstName || formData.shippingFirstName,
                billingLastName: formData.billingLastName || formData.shippingLastName,
                billingAddress: formData.billingAddress || formData.shippingAddress,
                billingCity: formData.billingCity || formData.shippingCity,
                billingState: formData.billingState || formData.shippingState,
                billingPostalCode: formData.billingPostalCode || formData.shippingPostalCode,
                billingCountry: formData.billingCountry || formData.shippingCountry,
                paymentMethod: formData.paymentMethod,
                shippingMethod: formData.shippingMethod,
                taxAmount: calculateSubtotal() * 0.1,
                shippingAmount: calculateSubtotal() > 100 ? 0 : 10,
                discountAmount: 0,
                notes: formData.notes,
                organizationID: 1
            };

            let order;
            if (isGuest && guestSessionId) {
                order = await PublicCartAPI.guestCheckout(guestSessionId, orderPayload);
                sessionStorage.removeItem('cartSessionId');
            } else {
                order = await CustomerOrdersAPI.createOrder(orderPayload);
            }

            notifyCartUpdated();
            toast.success('Order placed successfully!');
            navigate(`/order-confirmation/${order.orderID}`, { state: { order } });
        } catch (error) {
            console.error('Error creating order:', error);
            toast.error(error.response?.data?.message || 'Failed to place order');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return <div className="checkout-loading">Loading checkout...</div>;
    }

    if (!cart || !cart.cartItems || cart.cartItems.length === 0) {
        return null;
    }

    return (
        <div className="checkout-container">
            <h1>Checkout</h1>
            
            <form onSubmit={handleSubmit} className="checkout-form">
                <div className="checkout-content">
                    <div className="checkout-form-section">
                        {/* Shipping Information */}
                        <div className="form-section">
                            <h2>Shipping Information</h2>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name *</label>
                                    <input
                                        type="text"
                                        name="shippingFirstName"
                                        value={formData.shippingFirstName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name *</label>
                                    <input
                                        type="text"
                                        name="shippingLastName"
                                        value={formData.shippingLastName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Address *</label>
                                <input
                                    type="text"
                                    name="shippingAddress"
                                    value={formData.shippingAddress}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>City</label>
                                    <input
                                        type="text"
                                        name="shippingCity"
                                        value={formData.shippingCity}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>State</label>
                                    <input
                                        type="text"
                                        name="shippingState"
                                        value={formData.shippingState}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Postal Code</label>
                                    <input
                                        type="text"
                                        name="shippingPostalCode"
                                        value={formData.shippingPostalCode}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Country</label>
                                    <input
                                        type="text"
                                        name="shippingCountry"
                                        value={formData.shippingCountry}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone</label>
                                    <input
                                        type="tel"
                                        name="shippingPhone"
                                        value={formData.shippingPhone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Billing Information */}
                        <div className="form-section">
                            <h2>Billing Information</h2>
                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="billingSameAsShipping"
                                        checked={formData.billingSameAsShipping}
                                        onChange={handleInputChange}
                                    />
                                    Same as shipping address
                                </label>
                            </div>
                            
                            {!formData.billingSameAsShipping && (
                                <>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>First Name</label>
                                            <input
                                                type="text"
                                                name="billingFirstName"
                                                value={formData.billingFirstName}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Last Name</label>
                                            <input
                                                type="text"
                                                name="billingLastName"
                                                value={formData.billingLastName}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Address</label>
                                        <input
                                            type="text"
                                            name="billingAddress"
                                            value={formData.billingAddress}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>City</label>
                                            <input
                                                type="text"
                                                name="billingCity"
                                                value={formData.billingCity}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>State</label>
                                            <input
                                                type="text"
                                                name="billingState"
                                                value={formData.billingState}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Postal Code</label>
                                            <input
                                                type="text"
                                                name="billingPostalCode"
                                                value={formData.billingPostalCode}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Country</label>
                                        <input
                                            type="text"
                                            name="billingCountry"
                                            value={formData.billingCountry}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Payment & Shipping */}
                        <div className="form-section">
                            <h2>Payment & Shipping</h2>
                            <div className="form-group">
                                <label>Payment Method</label>
                                <select
                                    name="paymentMethod"
                                    value={formData.paymentMethod}
                                    onChange={handleInputChange}
                                >
                                    <option value="Credit Card">Credit Card</option>
                                    <option value="PayPal">PayPal</option>
                                    <option value="Cash on Delivery">Cash on Delivery</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Shipping Method</label>
                                <select
                                    name="shippingMethod"
                                    value={formData.shippingMethod}
                                    onChange={handleInputChange}
                                >
                                    <option value="Standard">Standard (5-7 days)</option>
                                    <option value="Express">Express (2-3 days)</option>
                                    <option value="Overnight">Overnight (1 day)</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Order Notes (Optional)</label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    rows="4"
                                    placeholder="Special instructions for your order..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className="order-summary-section">
                        <h2>Order Summary</h2>
                        <div className="order-items">
                            {cart.cartItems.map(item => (
                                <div key={item.cartItemID} className="order-item">
                                    <div className="item-info">
                                        <span className="item-name">{item.product?.name || 'Product'}</span>
                                        <span className="item-qty">Qty: {item.quantity}</span>
                                    </div>
                                    <span className="item-price">${(item.price * item.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="summary-totals">
                            <div className="summary-row">
                                <span>Subtotal:</span>
                                <span>${calculateSubtotal().toFixed(2)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Tax:</span>
                                <span>${(calculateSubtotal() * 0.1).toFixed(2)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Shipping:</span>
                                <span>{calculateSubtotal() > 100 ? 'Free' : '$10.00'}</span>
                            </div>
                            <div className="summary-row total">
                                <span>Total:</span>
                                <span>${calculateTotal().toFixed(2)}</span>
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="place-order-button"
                            disabled={processing}
                        >
                            {processing ? 'Processing...' : 'Place Order'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default Checkout;
